const ACTIVITY_VIEWS = ["all", "overdue", "today", "upcoming", "done"];
const SECTIONS = ["activities", "notifications"];
const QUICK_DATE_CHOICES = [
  "today",
  "tomorrow",
  "endOfWeek",
  "endOfMonth",
  "endOfQuarter",
  "endOfYear",
];

const timestampSeconds = (value, fallback) => {
  if (Number.isFinite(value)) return value;
  if (fallback?.toDate) return Math.floor(fallback.toDate().getTime() / 1000);
  if (fallback instanceof Date) return Math.floor(fallback.getTime() / 1000);
  return null;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const ENTITY_V2_PATHS = Object.freeze({
  animal: (id) => `/v2/individuals/${encodeURIComponent(id)}?section=notes`,
  enclosure: (id) => `/v2/enclosures/${encodeURIComponent(id)}?section=notes`,
  group: (id) => `/v2/groups/${encodeURIComponent(id)}?section=notes`,
  sector: (id) => `/v2/sectors/${encodeURIComponent(id)}?section=notes`,
});

const LEGACY_ENTITY_ROUTES = Object.freeze({
  "/AnimalManagementView": ["animal", "animalId"],
  "/EnclosureManagementView": ["enclosure", "enclosureId"],
  "/AdvancedGroupManagementView": ["group", "groupId"],
  "/SectorManagementView": ["sector", "sectorId"],
});

const entityDestination = (type, id) => {
  const builder = ENTITY_V2_PATHS[type];
  return builder && id ? builder(String(id)) : null;
};

// Domaines de production : le nouveau, puis l'ancien, qui sert toujours l'app et
// figure dans les notifications déjà enregistrées.
const PRODUCTION_ORIGINS = ["https://welfare-monitor.akongo.io", "https://welfare-manager.akongo.fr"];
const PRODUCTION_ORIGIN = PRODUCTION_ORIGINS[0];
// Sites Hosting des projets AKONGO (staging compris) : les liens écrits par les
// Functions hors production pointent vers `https://<projet>.web.app`.
const PROJECT_HOSTING_ORIGIN = /^https:\/\/aws-[a-z0-9-]+\.(web\.app|firebaseapp\.com)$/;

const currentOrigin = () => {
  try {
    return typeof window !== "undefined" && window.location?.origin ? window.location.origin : null;
  } catch (_error) {
    return null;
  }
};

const isApplicationOrigin = (origin) =>
  PRODUCTION_ORIGINS.includes(origin) || origin === currentOrigin() || PROJECT_HOSTING_ORIGIN.test(origin);

const canonicalDestination = (item) => {
  const href = item?.linkedEntity?.actionUrl || item?.actionUrl || null;
  if (typeof href !== "string" || !href.trim()) return null;

  let url;
  try {
    url = new URL(href, PRODUCTION_ORIGIN);
  } catch (_error) {
    return null;
  }

  // Seul le chemin sert : la navigation reste dans l'application ouverte,
  // quel que soit le domaine écrit dans le lien.
  if (!isApplicationOrigin(url.origin)) return null;
  if (url.pathname.startsWith("/v2/")) return `${url.pathname}${url.search}${url.hash}`;

  const legacyRoute = LEGACY_ENTITY_ROUTES[url.pathname];
  if (legacyRoute) {
    const [type, parameter] = legacyRoute;
    return entityDestination(type, url.searchParams.get(parameter));
  }

  const linkedEntity = item?.linkedEntity;
  // Rappel lié à une note : on ouvre les notes de l'entité qui porte la note
  // (individu, enclos, groupe, secteur), pas celles de tout le secteur.
  const directEntity = linkedEntity?.type === "note"
    ? entityDestination(linkedEntity.destinationType, linkedEntity.destinationId)
    : entityDestination(
      linkedEntity?.type || item?.destinationType,
      linkedEntity?.id || item?.destinationId,
    );
  if (directEntity) return directEntity;

  if (url.pathname === "/notes") {
    return entityDestination("sector", item?.sectorId || url.searchParams.get("sectorId"));
  }

  return null;
};

export const createActivitiesCore = ({ now = () => new Date() } = {}) => {
  const parseLocation = (location = {}) => {
    const parameters = new URLSearchParams(location.search || "");
    const pathname = location.pathname || "";
    const requestedSection = parameters.get("section");
    const section = SECTIONS.includes(requestedSection)
      ? requestedSection
      : pathname.includes("MyNotificationsView") ||
          pathname.endsWith("/notifications")
      ? "notifications"
      : "activities";
    const requestedView = parameters.get("view");

    return Object.freeze({
      section,
      activityId: parameters.get("activityId"),
      notificationId: parameters.get("notificationId"),
      filters: Object.freeze({
        view: ACTIVITY_VIEWS.includes(requestedView) ? requestedView : "all",
        search: parameters.get("q") || "",
        type: parameters.get("type"),
        destinationType: parameters.get("destinationType"),
        destinationId: parameters.get("destinationId"),
        zooId: parameters.get("zooId"),
      }),
    });
  };

  const normalizeActivity = (activity) =>
    Object.freeze({
      ...activity,
      id: activity.activityId || activity.id,
      status: activity.status === "done" ? "done" : "open",
      activityType: activity.activityType || "todo",
      dueAtUnix: timestampSeconds(activity.dueAtUnix, activity.dueAt),
      doneAtUnix: timestampSeconds(activity.doneAtUnix, activity.doneAt),
      linkedEntity: activity.linkedEntity || null,
    });

  const normalizeNotification = (notification) =>
    Object.freeze({
      ...notification,
      id: notification.id,
      unread: notification.status !== "acknowledged",
      createdAtUnix:
        timestampSeconds(notification.noteTimestamp) ||
        timestampSeconds(null, notification.createdAt),
    });

  const bucketFor = (activity) => {
    if (activity.status === "done") return "done";
    if (!activity.dueAtUnix) return "upcoming";
    const current = now();
    const currentSeconds = Math.floor(current.getTime() / 1000);
    if (activity.dueAtUnix < currentSeconds) return "overdue";
    const endOfDay = new Date(current);
    endOfDay.setHours(23, 59, 59, 999);
    return activity.dueAtUnix <= Math.floor(endOfDay.getTime() / 1000)
      ? "today"
      : "upcoming";
  };

  const filterActivities = (activities, filters = {}, scope = "zoo") => {
    const search = normalizeText(filters.search);
    return activities.filter((activity) => {
      const bucket = bucketFor(activity);
      if (filters.view === "done" && activity.status !== "done") return false;
      if (filters.view !== "done" && activity.status === "done") return false;
      if (!["all", "done"].includes(filters.view) && bucket !== filters.view) {
        return false;
      }
      if (filters.type && activity.activityType !== filters.type) return false;
      if (scope === "all" && filters.zooId && activity.zooId !== filters.zooId) {
        return false;
      }
      const entity = activity.linkedEntity;
      if (filters.destinationType === "none" && entity) return false;
      if (
        filters.destinationType &&
        filters.destinationType !== "none" &&
        entity?.type !== filters.destinationType
      ) {
        return false;
      }
      if (filters.destinationId && entity?.id !== filters.destinationId) {
        return false;
      }
      if (!search) return true;
      return normalizeText(
        [activity.title, activity.note, entity?.name].filter(Boolean).join(" ")
      ).includes(search);
    });
  };

  const groupActivities = (activities) => {
    const groups = { overdue: [], today: [], upcoming: [], done: [] };
    activities.forEach((activity) => groups[bucketFor(activity)].push(activity));
    return groups;
  };

  const getViewer = (raw = {}) => {
    const userId = raw.userId || raw.uid || raw.id || null;
    const isStaff = raw.akongoStaff === true || raw.akongoRank === "board";
    return Object.freeze({ userId, isStaff, canCrossZoo: Boolean(userId && isStaff) });
  };

  const destination = canonicalDestination;

  const quickDueDate = (choice) => {
    if (!QUICK_DATE_CHOICES.includes(choice)) {
      throw new TypeError("UNKNOWN_ACTIVITY_DATE_CHOICE");
    }
    const dueAt = new Date(now());
    if (choice === "today") {
      dueAt.setHours(dueAt.getHours() + 1, 0, 0, 0);
      return dueAt;
    }
    if (choice === "tomorrow") {
      dueAt.setDate(dueAt.getDate() + 1);
      dueAt.setHours(9, 0, 0);
      return dueAt;
    }
    if (choice === "endOfWeek") {
      const daysUntilSunday = (7 - dueAt.getDay()) % 7;
      dueAt.setDate(dueAt.getDate() + daysUntilSunday);
    } else if (choice === "endOfMonth") {
      dueAt.setMonth(dueAt.getMonth() + 1, 0);
    } else if (choice === "endOfQuarter") {
      const finalMonth = Math.floor(dueAt.getMonth() / 3) * 3 + 2;
      dueAt.setMonth(finalMonth + 1, 0);
    } else if (choice === "endOfYear") {
      dueAt.setMonth(11, 31);
    }
    dueAt.setHours(23, 59, 59, 999);
    dueAt.setHours(9, 0, 0);
    return dueAt;
  };

  return Object.freeze({
    parseLocation,
    normalizeActivity,
    normalizeNotification,
    filterActivities,
    groupActivities,
    bucketFor,
    getViewer,
    destination,
    quickDueDate,
  });
};

/** Destination V2 d'un rappel ou d'une notification (chemin interne, ou null). */
export const activityDestination = canonicalDestination;

export const ACTIVITIES_SECTIONS = SECTIONS;
export const ACTIVITIES_VIEWS = ACTIVITY_VIEWS;
export const ACTIVITY_QUICK_DATE_CHOICES = QUICK_DATE_CHOICES;
